'use client';

import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useAppSelector } from '@/store/hooks';
import { getUi, showModal } from '@/store/reducers/ui-slice';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Box, Stack } from '@mui/system';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Selection } from '@phosphor-icons/react/dist/ssr/Selection';
import { Control, Controller, ControllerRenderProps, DefaultValues, FieldValues, Path, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ZodSchema } from 'zod';

import { FieldType, InputType, ModalDrawer } from '@/types/general';
import { formatCurrencyCode } from '@/lib/helpers';

import HelpInfoPopover from './help-info-popover';
import { PasswordInput } from './password-field';
import { UploadCard } from './upload-card';

const TextEditorInput = dynamic(() => import('@/components/core/text-editor-input'), {
  ssr: false,
});

export function CustomConfigInput<TSchema extends FieldValues>(props: {
  fieldType: FieldType<TSchema>;
  schema: ZodSchema<TSchema>;
  defaultValues?: DefaultValues<TSchema>;
  control?: Control<TSchema>;
}): React.JSX.Element {
  const { fieldType, schema, defaultValues, control } = props;
  const { watch, reset } = useForm<TSchema>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
  });

  //side effect if fetching API for default values
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  let isHidden = fieldType.hidden || false;
  let menuItems = fieldType.menuItems || [];

  if (fieldType.dependencies) {
    fieldType.dependencies.forEach((dep) => {
      const watchedValue = watch(dep.field as Path<TSchema>);

      if (dep.action === 'hidden' && dep.condition(watchedValue)) {
        isHidden = true;
      }
      if (dep.action === 'menuItems' && Array.isArray(dep.value)) {
        // menuItems = dep.value;
        menuItems = dep.filter ? dep.value.filter((item) => dep.filter!(item, watchedValue)) : dep.value;
      }
    });
  }

  if (isHidden) return <></>;
  return (
    <Grid
      sx={{ width: 1 }}
      key={String(fieldType.name)}
      size={fieldType.size || { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
    >
      <Box
        sx={{
          width: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Controller
          control={control}
          name={fieldType.name as Path<TSchema>}
          rules={{ required: fieldType.required }}
          render={({ field, fieldState: { error } }) => (
            <FormControl hidden={fieldType.hidden} error={Boolean(error)} fullWidth>
              <Stack
                sx={{
                  width: 1,
                  alignItems: fieldType.direction === 'row' ? 'center' : 'start',
                  justifyContent: fieldType.direction === 'row' ? 'space-between' : 'start',
                }}
                direction={fieldType.direction || 'column'}
              >
                <Box sx={{ display: 'flex', gap: 0.8 }}>
                  {fieldType.type !== InputType.EDITOR ? (
                    <InputLabel required={fieldType.required}>{fieldType.label}</InputLabel>
                  ) : null}

                  {!fieldType.description ? null : <HelpInfoPopover content={fieldType.description} />}
                </Box>
                {InputField(field, { ...fieldType, menuItems })}
              </Stack>

              {error ? <FormHelperText>{error.message}</FormHelperText> : null}
            </FormControl>
          )}
        />
      </Box>
    </Grid>
  );
}

export function InputField<TSchema extends FieldValues>(
  field: ControllerRenderProps<TSchema, Path<TSchema>>,
  fieldType: FieldType<TSchema>
): React.JSX.Element {
  const dispatch = useDispatch();
  const { selectedPageItems } = useAppSelector(getUi);

  useEffect(() => {
    if (selectedPageItems?.target === field.name && selectedPageItems?.index === fieldType.id) {
      field.onChange(`${selectedPageItems.slug}`);
    }
  }, [selectedPageItems]);

  switch (fieldType.type) {
    case InputType.SELECT:
      return (
        <Select
          defaultValue={field.value}
          {...field}
          sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
          MenuProps={{
            sx: {
              zIndex: 1402,
            },
            slotProps: {
              paper: {
                style: {
                  maxHeight: 200,
                },
              },
            },
          }}
        >
          {fieldType?.menuItems?.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      );
    case InputType.MULTISELECT:
      return (
        <Select
          defaultValue={field.value || []}
          {...field}
          multiple
          sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Array.isArray(selected) && selected?.length
                ? selected?.map((value: string) => (
                    <Chip
                      key={value}
                      label={value}
                      color="default"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onDelete={(e) => {
                        e.stopPropagation();
                        field.onChange(selected.filter((item: string) => item !== value));
                      }}
                    />
                  ))
                : null}
            </Box>
          )}
          MenuProps={{
            sx: {
              zIndex: 1402,
            },
            slotProps: {
              paper: {
                style: {
                  maxHeight: 200,
                },
              },
            },
          }}
        >
          {fieldType?.menuItems?.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      );

    case InputType.INPUT:
      return (
        <OutlinedInput
          {...field}
          multiline={fieldType.multiline}
          sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
          endAdornment={fieldType.endAdornment}
          disabled={fieldType.disabled}
          required={fieldType.required}
        />
      );

    case InputType.PAGE_PICKER:
      return (
        <OutlinedInput
          {...field}
          multiline={fieldType.multiline}
          sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
          endAdornment={
            <InputAdornment position="end">
              <Button
                variant="text"
                size="small"
                sx={{ display: 'flex !important' }}
                onClick={() => {
                  dispatch(
                    showModal({
                      open: ModalDrawer.PAGE_PICKER,
                      content: {
                        target: fieldType.name,
                      },
                    })
                  );
                }}
                color="success"
                startIcon={<Selection />}
              >
                Pick Page
              </Button>
            </InputAdornment>
          }
          disabled={fieldType.disabled}
          required={fieldType.required}
        />
      );
    case InputType.NUMBER:
      return (
        <OutlinedInput
          {...field}
          type="number"
          sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
          disabled={fieldType.disabled}
          required={fieldType.required}
          endAdornment={
            fieldType.numberProps?.currency ? (
              <Typography>
                {fieldType.numberProps.currency === 'USD' ? '$' : formatCurrencyCode(fieldType.numberProps.currency)}
              </Typography>
            ) : fieldType.numberProps?.isPercentage ? (
              <Typography>%</Typography>
            ) : null
          }
        />
      );
    case InputType.PASSWORD:
      return <PasswordInput field={field} width={fieldType.width} required={fieldType.required} />;

    case InputType.MULTILINE_INPUT:
      return (
        <OutlinedInput
          multiline
          disabled={fieldType.disabled}
          {...field}
          sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
        />
      );
    case InputType.DATE_PICKER:
      return (
        <LocalizationProvider {...field} dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ width: fieldType.width || '100%', maxWidth: '100%' }}
            slotProps={{
              popper: {
                sx: { zIndex: 1500 },
              },
            }}
          />
        </LocalizationProvider>
      );

    case InputType.BOOLEAN:
      return (
        <FormControlLabel
          control={<Switch {...field} defaultChecked={field.value} disabled={fieldType.disabled} />}
          label={
            fieldType.trueLabel || fieldType.falseLabel
              ? field.value
                ? fieldType.trueLabel
                : fieldType.falseLabel
              : undefined
          }
        />
      );

    case InputType.IMAGE:
      return (
        <UploadCard
          id={fieldType.id || 1}
          width={fieldType.width}
          customKey={fieldType.customKey}
          recommendedDimensions={fieldType.recommendedDimensions}
          image={fieldType.image}
          onUpload={() => {
            // empty block
          }}
        />
      );
    case InputType.EDITOR:
      return (
        <TextEditorInput
          initialContent={field.value}
          label={fieldType.label}
          required={fieldType.required}
          name={field.name}
          onChange={(e) => {
            field.onChange(e.value);
          }}
        />
      );
    default:
      return <OutlinedInput {...field} sx={{ width: fieldType.width || '100%', maxWidth: '100%' }} />;
  }
}
